// Home screen — one /api/dashboard call (turnaround check-status) drives
// ready-to-view scans, pending orders with turnaround estimates, and unpaid
// invoices. Downloads and payments are surfaced inline as links.
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useRouter } from "../lib/router";
import type { Dashboard as DashboardData, PendingOrder, ReadyOrder, UnpaidInvoice } from "../lib/types";
import { Card, Spinner, EmptyState, ScreenHeader, Money } from "./ui";

function rollSummary(o: PendingOrder): string {
  const parts: string[] = [];
  const c = (o.color35 || 0) + (o.color120 || 0);
  const b = (o.bw35 || 0) + (o.bw120 || 0);
  if (c) parts.push(`${c} color`);
  if (b) parts.push(`${b} B&W`);
  return parts.join(" · ") || "Film order";
}

function ReadyCard({ o, onOpen }: { o: ReadyOrder; onOpen: () => void }) {
  const rolls = (o.colorRolls || 0) + (o.bwRolls || 0);
  return (
    <Card className="p-4 flex flex-col gap-3">
      <button onClick={onOpen} className="flex items-start justify-between gap-3 text-left">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[12px] font-medium text-sev-success bg-green-50 px-2 py-0.5 rounded-full">
              <i className="ph-fill ph-check-circle" /> Ready
            </span>
            {o.dropDate && <span className="text-[12px] text-ink-subtle">Dropped {o.dropDate}</span>}
          </div>
          <div className="text-ink font-medium mt-1.5">{o.message || `${rolls} roll${rolls === 1 ? "" : "s"} scanned`}</div>
        </div>
        <i className="ph ph-caret-right text-ink-faint mt-1" />
      </button>
      <div className="flex gap-2">
        {o.downloadUrl && (
          <a href={o.downloadUrl} className="flex-1 h-11 rounded-xl bg-navy text-white text-[15px] font-medium inline-flex items-center justify-center gap-2">
            <i className="ph ph-download-simple" /> Download scans
          </a>
        )}
        {o.payUrl && (
          <a href={o.payUrl} className="flex-1 h-11 rounded-xl bg-orange text-white text-[15px] font-medium inline-flex items-center justify-center gap-2">
            <i className="ph ph-credit-card" /> Pay {o.amount ? `$${o.amount}` : null}
          </a>
        )}
      </div>
    </Card>
  );
}

function formatEstDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function PendingCard({ o, onOpen }: { o: PendingOrder; onOpen: () => void }) {
  const t = o.turnaround || {};
  const est = formatEstDate(t.estimatedFinalDate);
  const messages = t.specialMessages || [];
  const rollsAhead = t.rollsAheadInQueue;
  const daysInLab = t.workDaysSinceDropoff;
  const isMailIn = o.orderType === "mail-in";
  return (
    <button onClick={onOpen} className="w-full text-left">
      <Card className="p-4 flex flex-col gap-1.5 active:bg-line-soft/40">
        <div className="flex items-center justify-between gap-2">
          <span className="text-ink font-medium flex items-center gap-2">
            {rollSummary(o)}
            {isMailIn && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-navy bg-navy-soft px-1.5 py-0.5 rounded">
                <i className="ph-fill ph-mailbox" /> Mail-in
              </span>
            )}
          </span>
          <span className="flex items-center gap-1.5">
            {o.dropDate && <span className="text-[12px] text-ink-subtle">Dropped {o.dropDate}</span>}
            <i className="ph ph-caret-right text-ink-faint" />
          </span>
        </div>
        <div className="flex items-center gap-2 text-[13px] text-ink-muted">
          <i className="ph ph-flask text-navy" />
          <span>{est ? `Estimated ready ${est}` : "In the lab"}</span>
          {o.scanResolution && <span className="text-ink-subtle">· {o.scanResolution}</span>}
        </div>
        {(rollsAhead != null || daysInLab != null) && (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-ink-subtle">
            {rollsAhead != null && (
              <span className="inline-flex items-center gap-1">
                <i className="ph ph-list-numbers" /> {rollsAhead} roll{rollsAhead === 1 ? "" : "s"} ahead in queue
              </span>
            )}
            {daysInLab != null && (
              <span className="inline-flex items-center gap-1">
                <i className="ph ph-hourglass-medium" /> {daysInLab} business day{daysInLab === 1 ? "" : "s"} in the lab
              </span>
            )}
          </div>
        )}
        {t.stillWorkingToday === false && <div className="text-[12px] text-ink-subtle">The lab is done processing for today.</div>}
        {messages.map((m, i) => (
          <div key={i} className="text-[12px] text-ink-muted leading-snug">
            {m}
          </div>
        ))}
        {t.priorityRequestSent && <div className="text-[12px] text-orange">Priority requested</div>}
      </Card>
    </button>
  );
}

function UnpaidCard({ inv, onOpen }: { inv: UnpaidInvoice; onOpen: () => void }) {
  return (
    <Card className="p-4 flex items-center justify-between gap-3">
      <button onClick={onOpen} className="text-left flex-1">
        <div className="text-ink font-medium">{inv.description || "Invoice"}</div>
        <div className="text-[13px] text-ink-subtle">
          ${inv.amount} due
        </div>
      </button>
      <a href={inv.hostedUrl} className="h-10 px-4 rounded-xl bg-orange text-white text-[14px] font-medium inline-flex items-center gap-2">
        <i className="ph ph-credit-card" /> Pay
      </a>
    </Card>
  );
}

function Section({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  return (
    <section className="px-4 mb-5">
      <h2 className="text-[13px] font-semibold uppercase tracking-wide text-ink-subtle px-1 mb-2">
        {title}
        {count != null && count > 0 && <span className="ml-1.5 text-ink-faint">{count}</span>}
      </h2>
      <div className="flex flex-col gap-2.5">{children}</div>
    </section>
  );
}

export function Dashboard({ email }: { email: string }) {
  const { navigate } = useRouter();
  const go = (id: string) => navigate(`/order/${encodeURIComponent(id)}`);
  const { data, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get<DashboardData>("/api/dashboard"),
    staleTime: 60 * 1000,
  });

  return (
    <div className="pb-4">
      <ScreenHeader
        title="Your film"
        subtitle={email}
        right={
          <button onClick={() => refetch()} className="h-9 w-9 rounded-full text-ink-muted active:bg-line-soft" aria-label="Refresh">
            <i className={`ph ph-arrows-clockwise text-lg ${isRefetching ? "animate-spin" : ""}`} />
          </button>
        }
      />

      {isLoading && <Spinner label="Checking on your film…" />}
      {isError && (
        <EmptyState icon="ph-warning-circle" title="Couldn't load your orders" hint="Pull to refresh or try again in a moment." />
      )}

      {data && (
        <>
          {data.readyOrders.length > 0 && (
            <Section title="Ready now" count={data.readyOrders.length}>
              {data.readyOrders.map((o) => (
                <ReadyCard key={o.invoiceId} o={o} onOpen={() => go(o.invoiceId)} />
              ))}
            </Section>
          )}

          {data.unpaidInvoices.length > 0 && (
            <Section title="To pay" count={data.unpaidInvoices.length}>
              {data.unpaidInvoices.map((inv) => (
                <UnpaidCard key={inv.id} inv={inv} onOpen={() => go(inv.id)} />
              ))}
            </Section>
          )}

          {data.pendingOrders.length > 0 && (
            <Section title="In the lab" count={data.pendingOrders.length}>
              {data.pendingOrders.map((o) => (
                <PendingCard key={o.invoiceId} o={o} onOpen={() => go(o.invoiceId)} />
              ))}
            </Section>
          )}

          {data.readyOrders.length === 0 &&
            data.pendingOrders.length === 0 &&
            data.unpaidInvoices.length === 0 && (
              <EmptyState
                icon="ph-camera"
                title="No active orders"
                hint="When you drop off film, it'll show up here with live turnaround estimates."
              />
            )}
        </>
      )}
    </div>
  );
}
